import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import { PlusOutlined } from '@ant-design/icons';
import { remove, uniq } from 'lodash';
import Tag from 'antd/lib/tag';
import { colorWithAlpha, rwColors } from '../styles/globalStyles';
import { selectAuthCredentials } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import * as API from '../../api';
import { OrgComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { parseEmailList } from '../../helpers/formHelpers';

// Keep in sync with maxEmailInvitesInBatch in lib/runway/email_invite.go.
const MAX_EMAIL_INVITES_PER_BATCH = 50;

interface InviteTeamModalProps extends OrgComponentProps {
    open: boolean;
    onCancel: () => void;
}

const InviteTeamModal: React.FC<InviteTeamModalProps> = (props: InviteTeamModalProps) => {
    const [form] = Form.useForm();
    const [emailsToInvite, setEmailsToInvite] = React.useState<string[]>([]);
    const [sendEmailsIsLoading, setSendEmailsIsLoading] = React.useState(false);

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const setEmailInputError = (error: string, value?: string) => {
        form.setFields([{ name: 'email', value: value ?? form.getFieldValue('email'), errors: [error] }]);
    };

    const addEmailToList = (values) => {
        const { valid, invalid } = parseEmailList(values.email);
        const alreadyAdded = new Set(emailsToInvite.map((email) => email.toLowerCase()));
        const newEmails = valid.filter((email) => !alreadyAdded.has(email.toLowerCase()));

        const remainingSlots = MAX_EMAIL_INVITES_PER_BATCH - emailsToInvite.length;
        if (newEmails.length > remainingSlots) {
            setEmailInputError(
                `You can invite up to ${MAX_EMAIL_INVITES_PER_BATCH} people at once (${remainingSlots} remaining)`,
            );
            return;
        }

        if (newEmails.includes('adrian@runway.team')) {
            globalMessage.info("ceci n'est pas un œuf de Pâques");
            globalMessage.info('È ora il 2023 e questo non è ancora un uovo di Pasqua.');
            globalMessage.info('Τώρα είναι το 2024 και αυτό δεν είναι ακόμα ένα αυγό του Πάσχα.');
            globalMessage.info('今は2025年で、これはまだイースターエッグではありません。');
            globalMessage.info('É agora 2026 e isto ainda não é um ovo de Páscoa.');
        }

        setEmailsToInvite([...emailsToInvite, ...newEmails]);

        if (invalid.length > 0) {
            // Leave only the rejected entries in the input so they can be fixed and re-added.
            setEmailInputError(`Please enter a valid email: ${invalid.join(', ')}`, invalid.join(', '));
        } else if (newEmails.length === 0) {
            setEmailInputError('Email already added');
        } else {
            form.setFieldsValue({ email: null });
        }
    };

    const removeEmailFromList = (email) => {
        const updatedEmailsToInvite = [...emailsToInvite];

        remove(updatedEmailsToInvite, (inviteEmail) => inviteEmail === email);

        setEmailsToInvite(uniq(updatedEmailsToInvite));

        form.setFieldsValue({ email: null });
    };

    const sendEmailInvites = () => {
        setSendEmailsIsLoading(true);

        API.inviteTeam({ orgId: props.orgId, emails: emailsToInvite }, authCredentials!)
            .then(() => {
                globalMessage.success('Invite emails sent!');

                props.onCancel();
                return;
            })
            .catch((e: Error) => {
                globalMessage.error(e.message);
                return;
            })
            .finally(() => {
                setSendEmailsIsLoading(false);
            });
    };

    return (
        <Modal
            title="Invite your team"
            okButtonProps={{ disabled: emailsToInvite.length === 0, loading: sendEmailsIsLoading }}
            onOk={sendEmailInvites}
            okText="Send email invites"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                form.resetFields();
                setEmailsToInvite([]);
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>
                    Send invites to your team to join your Runway organization. Separate multiple emails with commas.
                </Text>
                <Form onFinish={addEmailToList} initialValues={{ email: null }} style={{ marginTop: 24 }} form={form}>
                    <Space style={{ width: '100%' }} align="start">
                        <Form.Item
                            validateTrigger="onFinish"
                            name="email"
                            rules={[{ required: true, message: 'At least one email address is required' }]}
                        >
                            <Input
                                placeholder={'email@yourdomain.com, email2@yourdomain.com'}
                                width={430}
                                autoFocus
                                allowClear
                                autoComplete="off"
                            />
                        </Form.Item>
                        <Button onClick={() => form.submit()} size="middle" type="primary" icon={<PlusOutlined />}>
                            Add
                        </Button>
                    </Space>
                </Form>
                {emailsToInvite.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text>Inviting users:</Text>
                        <Space wrap style={{ marginTop: 12 }}>
                            {emailsToInvite.map((email) => {
                                return (
                                    <Tag
                                        key={email}
                                        closable
                                        onClose={() => removeEmailFromList(email)}
                                        color={colorWithAlpha(rwColors.primary, 0.8)}
                                    >
                                        {email}
                                    </Tag>
                                );
                            })}
                        </Space>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default InviteTeamModal;
